import { createSlice } from '@reduxjs/toolkit';
import { ObjectTypes } from '../../utils/constants';
import { parseJwt } from '../../utils/jwt';

const extractUserObjects = (userObjects) => {
  const userObjs = {
    assignedDivisions: [],
    assignedClients: [],
    assignedLocations: [],
    assignedSourceSystems: [],
    assignedProjects: [],
    assignedSuppliers: [],
    assignedContracts: [],
    assignedFleets: [],
    assignedScenarios: [],
    assignedAddressTypes: []
  };

  if (!Array.isArray(userObjects) || userObjects.length === 0) {
    return userObjs;
  }

  const getConfigData = (objectType) => {
    const configData = userObjects.find((x) => x.ObjectType === objectType);
    if (configData && configData.ObjectData) {
      return JSON.parse(configData.ObjectData);
    }

    return null;
  };

  userObjs.assignedDivisions = getConfigData(ObjectTypes.AssignedDivisions) || [];
  userObjs.assignedClients = getConfigData(ObjectTypes.AssignedClients) || [];
  userObjs.assignedLocations = getConfigData(ObjectTypes.AssignedLocations) || [];
  userObjs.assignedSourceSystems = getConfigData(ObjectTypes.AssignedSourceSystems) || [];
  userObjs.assignedProjects = getConfigData(ObjectTypes.AssignedProjects) || [];
  userObjs.assignedSuppliers = getConfigData(ObjectTypes.AssignedSuppliers) || [];
  userObjs.assignedContracts = getConfigData(ObjectTypes.AssociateContracts) || [];
  userObjs.assignedFleets = getConfigData(ObjectTypes.FleetAssociates) || [];
  userObjs.assignedScenarios = getConfigData(ObjectTypes.AssignedScenarios) || [];
  userObjs.assignedAddressTypes = getConfigData(ObjectTypes.AssignedAddressTypes) || [];
  return userObjs;
};
const parsePermissions = (permissions) => {
  const permissionSet = [];
  try {
    if (!Array.isArray(permissions) || permissions.length === 0) {
      return permissionSet;
    }
    // permissions.forEach((p) => permissionSet.push(p.toLowerCase()));
    const permissionCodes = permissions.map((permissionClaim) => permissionClaim.Code) || [];
    // console.log('Parsed permission codes: ', permissionCodes);
    return permissionCodes.reduce((previousValue, currentValue) => {
      if (currentValue) {
        previousValue[currentValue.toLowerCase()] = true;
      }
      return previousValue;
    }, {});
  } catch (error) {
    console.log(error);
  }

  return permissionSet;
};

const parseRoleClaims = (roleClaims) => {
  const emptySet = {};
  try {
    if (!Array.isArray(roleClaims) || roleClaims.length === 0) {
      return emptySet;
    }

    const permissionClaim = roleClaims.find(
      (c) => c.ClaimName === 'Permission'
    );
    if (!permissionClaim) {
      return emptySet;
    }

    const permissionCodes = JSON.parse(permissionClaim.ClaimValue) || [];
    return permissionCodes.reduce((previousValue, currentValue) => {
      if (currentValue) {
        previousValue[currentValue.toLowerCase()] = true;
      }
      return previousValue;
    }, {});
  } catch (error) {
    console.log(error);
  }

  return emptySet;
};

const initialState = {
  isAuthenticated: false,
  loginUser: null,
  impersonatedUser: null,
  isValidatingToken: false,
  isImpersonateing: false
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // updateAuthData(state, action) {
    //   Object.keys(initialState).forEach((key) => {
    //     state[key] = action.payload[key];
    //   });
    // },

    updateLoginUser(state, action) {
      const {
        isAuthenticated,
        user,
        roleClaims,
        userToRoles,
        permissions,
        customPermissions,
        userAddressList,
        userContactList,
        userGlobalFilterPetroHub,
        isPetroHubUser,
        token,
        userObjects,
        dateFormat,
        dateTimeFormat,
        isClientOnHold,
      } = action.payload;
      const {
        assignedDivisions,
        assignedClients,
        assignedLocations,
        assignedSourceSystems,
        assignedProjects,
        assignedSuppliers,
        assignedContracts,
        assignedFleets,
        assignedScenarios,
        assignedAddressTypes
      } = extractUserObjects(userObjects);

      const { uname: username } = parseJwt(token) || {};

      state.isAuthenticated = isAuthenticated;
      // permissions?.push({ Code: 'UM', Options: ['S', 'I', 'M', 'E', 'D'] });
      // permissions?.push({ Code: 'RM', Options: ['S', 'I', 'M', 'E', 'D'] });
      // permissions?.push({ Code: 'SS', Options: ['S', 'I', 'M', 'E', 'D'] });
      // permissions?.push({ Code: 'ObjectConfigurations', Options: ['S', 'I', 'M', 'E', 'D'] }); // ObjectConfigurations: 'ObjectConfigurations',
      const permissionSet = isPetroHubUser && permissions && permissions.length > 0 ? parsePermissions(permissions) : parseRoleClaims(roleClaims);

      // const permissions = parsePermissions(permissions);
      // const userToRoles = parseUserToRoles(userToRoles);
      // console.log(customPermissions, 'customPermissions in auth slice');
      state.loginUser = {
        user,
        username,
        assignedDivisions,
        assignedClients,
        assignedLocations,
        assignedSourceSystems,
        assignedProjects,
        assignedSuppliers,
        assignedContracts,
        assignedFleets,
        assignedScenarios,
        assignedAddressTypes,
        dateFormat,
        dateTimeFormat,
        permissionSet,
        permissions,
        customPermissions,
        userToRoles,
        userAddressList,
        userContactList,
        isPetroHubUser,
        userGlobalFilterPetroHub,
        isClientOnHold,
        roleClaims
      };
    },
    logout(state) {
      state.isAuthenticated = false;
      state.loginUser = null;
    },
    startImpersonating(state, action) {
      const {
        UserAccountID,
        UserID,
        Username,
        dateFormat,
        dateTimeFormat,
        user,
        roleClaims,
        userObjects,
        permissions,
        customPermissions,
        userAddressList,
        userContactList,
        isPetroHubUser,
        userGlobalFilterPetroHub,
        userToRoles
      } = action.payload;

      const {
        assignedDivisions,
        assignedClients,
        assignedLocations,
        assignedSourceSystems,
        assignedProjects,
        assignedSuppliers,
        assignedContracts,
        assignedFleets,
        assignedScenarios,
        assignedAddressTypes
      } = extractUserObjects(userObjects);

      const permissionSet = isPetroHubUser && permissions && permissions.length > 0 ? parsePermissions(permissions) : parseRoleClaims(roleClaims);

      state.impersonatedUser = {
        UserAccountID,
        UserID,
        Username,
        dateFormat,
        dateTimeFormat,
        user,
        assignedDivisions,
        assignedClients,
        assignedContracts,
        assignedFleets,
        assignedLocations,
        assignedSourceSystems,
        assignedProjects,
        assignedSuppliers,
        assignedScenarios,
        assignedAddressTypes,
        permissionSet,
        permissions,
        userToRoles,
        userAddressList,
        userContactList,
        isPetroHubUser,
        userGlobalFilterPetroHub,
        customPermissions
      };

      state.isImpersonateing = true;
    },
    stopImpersonating(state) {
      state.isImpersonateing = false;
      state.impersonatedUser = null;
    },
    updateProfileInfo(state, action) {
      const { user } = action.payload;
      state.loginUser.user = user;
      state.loginUser.dateFormat = user.DateFormat;
      state.loginUser.dateTimeFormat = user.DateTimeFormat;
      state.loginUser.userAddressList = user.UserAddressList;
      state.loginUser.userContactList = user.UserContactList;
    },
    updateUserGlobalFilterPetroHub(state, action) {
      // if (state.loginUser) {
      state.loginUser.userGlobalFilterPetroHub = action.payload;
      // }
    },
    updateValidatingToken(state, action) {
      state.isValidatingToken = action.payload;
    },
  },
});

export const authActions = authSlice.actions;
export default authSlice.reducer;
